import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { cryptoAPI } from '../services/api';
import './ClubFund.css';

const CRYPTO_ICONS = {
  BTC: '₿',
  ETH: 'Ξ',
  SOL: '◎',
  XRP: '✕',
  DOGE: 'Ð',
};

const CRYPTO_COLORS = {
  BTC: '#f7931a',
  ETH: '#627eea',
  SOL: '#9945ff',
  XRP: '#23292f',
  DOGE: '#c2a633',
};

function getCryptoIcon(symbol) {
  return CRYPTO_ICONS[symbol?.toUpperCase()] || symbol?.charAt(0) || '?';
}

function getCryptoColor(symbol) {
  return CRYPTO_COLORS[symbol?.toUpperCase()] || '#f0c040';
}

function ClubCrypto({ clubId: propClubId, embedded = false }) {
  const params = useParams();
  const clubId = propClubId || params.clubId;
  const [assets, setAssets] = useState([]);
  const [holdings, setHoldings] = useState(null);
  const [memberShare, setMemberShare] = useState(null);
  const [proposals, setProposals] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('markets');
  const [buyModal, setBuyModal] = useState(null);
  const [buyAmount, setBuyAmount] = useState('');
  const [buyLoading, setBuyLoading] = useState(false);
  const [buyError, setBuyError] = useState('');
  const [voteLoading, setVoteLoading] = useState({});
  const [allShares, setAllShares] = useState([]);
  const [showMembers, setShowMembers] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const formatCurrency = (cents) => `$${(parseFloat(cents) / 100).toFixed(2)}`;
  const formatAud = (val) => {
    const dollars = parseFloat(val) || 0;
    if (dollars >= 1000000) return `$${(dollars / 1000000).toFixed(2)}M`;
    if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}K`;
    return `$${dollars.toFixed(2)}`;
  };
  const formatCrypto = (val) => {
    const qty = parseFloat(val) || 0;
    if (qty === 0) return '0';
    if (qty < 0.0001) return qty.toExponential(2);
    if (qty < 1) return qty.toFixed(6);
    if (qty < 100) return qty.toFixed(4);
    return qty.toFixed(2);
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Always fetch assets (public endpoint)
      const assetsRes = await cryptoAPI.getAssets().catch(() => ({}));
      setAssets(assetsRes.assets || []);

      // Club-scoped endpoints — silently handle failures
      if (clubId) {
        const [holdingsRes, memberShareRes, proposalsRes, transactionsRes, allSharesRes] = await Promise.allSettled([
          cryptoAPI.getHoldings(clubId),
          cryptoAPI.getMemberShare(clubId),
          cryptoAPI.getProposals(clubId),
          cryptoAPI.getTransactions(clubId),
          cryptoAPI.getAllShares(clubId),
        ]);
        setHoldings(holdingsRes.status === 'fulfilled' && holdingsRes.value?.success !== false ? holdingsRes.value : null);
        setMemberShare(memberShareRes.status === 'fulfilled' && memberShareRes.value?.success !== false ? memberShareRes.value : null);
        setProposals(proposalsRes.status === 'fulfilled' ? (proposalsRes.value.proposals || []) : []);
        setTransactions(transactionsRes.status === 'fulfilled' ? (transactionsRes.value.transactions || []) : []);
        setAllShares(allSharesRes.status === 'fulfilled' ? (allSharesRes.value.members || []) : []);
      }
    } catch (err) {
      console.error('Error loading crypto data:', err);
    } finally {
      setLoading(false);
    }
  }, [clubId, embedded]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreateProposal = async () => {
    if (!buyModal || !buyAmount) return;
    setBuyLoading(true);
    setBuyError('');
    try {
      const amountCents = Math.round(parseFloat(buyAmount) * 100);
      if (isNaN(amountCents) || amountCents <= 0) {
        setBuyError('Enter a valid amount.');
        setBuyLoading(false);
        return;
      }
      await cryptoAPI.createProposal(clubId, {
        action: 'buy',
        asset: buyModal.base_currency || buyModal.symbol,
        productId: buyModal.product_id || buyModal.productId || buyModal.id,
        amountCents,
      });
      setBuyModal(null);
      setBuyAmount('');
      await loadData();
    } catch (err) {
      setBuyError(err.response?.data?.error || 'Failed to create proposal.');
    } finally {
      setBuyLoading(false);
    }
  };

  const handleVote = async (proposalId, vote) => {
    setVoteLoading((prev) => ({ ...prev, [proposalId]: true }));
    try {
      await cryptoAPI.voteOnProposal(clubId, proposalId, vote);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to vote.');
    } finally {
      setVoteLoading((prev) => ({ ...prev, [proposalId]: false }));
    }
  };

  // --- Render helpers ---

  const renderAssetCard = (asset, compact = false) => {
    const symbol = asset.base_currency || asset.symbol || asset.code || '';
    const price = asset.price || asset.priceAud || 0;
    const change24h = asset.price_change_24h || asset.change24h || 0;
    const isUp = change24h >= 0;

    return (
      <div
        key={asset.productId || asset.id || symbol}
        style={{
          backgroundColor: 'var(--surface, #1e293b)',
          borderRadius: '1rem',
          border: '1px solid var(--border, #334155)',
          padding: compact ? '12px' : '16px',
          overflow: 'hidden',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          minWidth: compact ? '140px' : 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: compact ? 32 : 40, height: compact ? 32 : 40, borderRadius: '50%',
              backgroundColor: getCryptoColor(symbol), color: '#fff',
              fontSize: compact ? '14px' : '18px', fontWeight: '700',
            }}>
              {getCryptoIcon(symbol)}
            </span>
            <div>
              <div style={{ color: 'var(--text-primary, #fff)', fontWeight: '600', fontSize: compact ? '13px' : '15px' }}>{symbol}</div>
              {!compact && <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px' }}>{asset.base_name || asset.name || symbol}</div>}
            </div>
          </div>
        </div>
        <div style={{ marginBottom: '8px' }}>
          <div style={{ color: 'var(--text-primary, #fff)', fontSize: compact ? '15px' : '18px', fontWeight: '700' }}>
            {formatAud(price)}
          </div>
          <div style={{ color: isUp ? '#16a34a' : '#ef4444', fontSize: '13px', fontWeight: '600' }}>
            {isUp ? '+' : ''}{parseFloat(change24h).toFixed(2)}%
          </div>
        </div>
        <button
          onClick={() => setBuyModal(asset)}
          style={{
            width: '100%', padding: '6px 12px', borderRadius: '6px', border: 'none',
            fontSize: '13px', fontWeight: '600', cursor: 'pointer',
            backgroundColor: '#f0c040', color: '#1a1d23',
          }}
        >
          Buy
        </button>
      </div>
    );
  };

  const renderHoldingRow = (holding) => {
    const symbol = holding.asset || holding.symbol || '';
    const qty = parseFloat(holding.totalQuantity || holding.quantity || 0);
    const avgPrice = holding.totalCostCents && qty > 0 ? (holding.totalCostCents / 100) / qty : (holding.avgBuyPrice || 0);
    const currentPrice = holding.spotPriceDollars || holding.currentPrice || 0;
    const currentValue = holding.currentValueDollars || holding.currentValueAud || (qty * currentPrice);
    const costBasis = holding.totalCostDollars || holding.costBasisAud || 0;
    const pnl = currentValue - costBasis;
    const pnlPct = costBasis > 0 ? (pnl / costBasis) * 100 : 0;
    const isUp = pnl >= 0;

    return (
      <div
        key={symbol}
        style={{
          display: 'flex', alignItems: 'center', padding: '12px 16px',
          borderBottom: '1px solid var(--border, #334155)',
          gap: '8px', flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 120px', minWidth: '100px' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: '50%',
            backgroundColor: getCryptoColor(symbol), color: '#fff',
            fontSize: '12px', fontWeight: '700',
          }}>
            {getCryptoIcon(symbol)}
          </span>
          <span style={{ color: 'var(--text-primary, #fff)', fontWeight: '600', fontSize: '14px' }}>{symbol}</span>
        </div>
        <div style={{ flex: '1 1 80px', minWidth: '70px' }}>
          <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '11px' }}>Qty</div>
          <div style={{ color: 'var(--text-primary, #fff)', fontSize: '13px' }}>{formatCrypto(qty)}</div>
        </div>
        <div style={{ flex: '1 1 80px', minWidth: '70px' }}>
          <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '11px' }}>Avg Price</div>
          <div style={{ color: 'var(--text-primary, #fff)', fontSize: '13px' }}>{formatAud(avgPrice)}</div>
        </div>
        <div style={{ flex: '1 1 80px', minWidth: '70px' }}>
          <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '11px' }}>Price</div>
          <div style={{ color: 'var(--text-primary, #fff)', fontSize: '13px' }}>{formatAud(currentPrice)}</div>
        </div>
        <div style={{ flex: '1 1 80px', minWidth: '70px' }}>
          <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '11px' }}>Value</div>
          <div style={{ color: 'var(--text-primary, #fff)', fontSize: '13px', fontWeight: '600' }}>{formatAud(currentValue)}</div>
        </div>
        <div style={{ flex: '1 1 90px', minWidth: '80px', textAlign: 'right' }}>
          <div style={{ color: isUp ? '#16a34a' : '#ef4444', fontSize: '14px', fontWeight: '600' }}>
            {isUp ? '+' : ''}{formatAud(pnl)}
          </div>
          <div style={{ color: isUp ? '#16a34a' : '#ef4444', fontSize: '12px' }}>
            {isUp ? '+' : ''}{pnlPct.toFixed(1)}%
          </div>
        </div>
      </div>
    );
  };

  const renderProposalCard = (proposal) => {
    const votesFor = proposal.votesFor || 0;
    const votesAgainst = proposal.votesAgainst || 0;
    const votesRequired = proposal.votesRequired || 1;
    const totalVotes = votesFor + votesAgainst;
    const pct = votesRequired > 0 ? Math.min((votesFor / votesRequired) * 100, 100) : 0;
    const isPending = proposal.status === 'pending' || proposal.status === 'open';
    const expiresAt = proposal.expiresAt ? new Date(proposal.expiresAt) : null;
    const now = new Date();
    const expiresIn = expiresAt ? Math.max(0, Math.floor((expiresAt - now) / (1000 * 60 * 60))) : null;

    return (
      <div
        key={proposal.id}
        style={{
          backgroundColor: 'var(--surface, #1e293b)',
          borderRadius: '1rem',
          border: '1px solid var(--border, #334155)',
          padding: '14px',
          marginBottom: '10px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 28, height: 28, borderRadius: '50%',
              backgroundColor: getCryptoColor(proposal.asset), color: '#fff',
              fontSize: '12px', fontWeight: '700',
            }}>
              {getCryptoIcon(proposal.asset)}
            </span>
            <div>
              <span style={{ color: 'var(--text-primary, #fff)', fontWeight: '600', fontSize: '14px' }}>
                {(proposal.action || 'buy').toUpperCase()} {proposal.asset}
              </span>
              <span style={{ color: 'var(--text-secondary, #64748b)', fontSize: '13px', marginLeft: '8px' }}>
                {formatCurrency(proposal.amountCents || 0)}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            {isPending && expiresIn !== null && (
              <span style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px' }}>
                {expiresIn > 0 ? `${expiresIn}h left` : 'Expiring'}
              </span>
            )}
            <span style={{
              padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600',
              backgroundColor: isPending ? 'rgba(240,192,64,0.15)' : proposal.status === 'executed' ? 'rgba(22,163,74,0.15)' : 'rgba(239,68,68,0.15)',
              color: isPending ? '#f0c040' : proposal.status === 'executed' ? '#16a34a' : '#ef4444',
            }}>
              {proposal.status || 'pending'}
            </span>
          </div>
        </div>
        {proposal.proposerName && (
          <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px', marginBottom: '6px' }}>
            Proposed by {proposal.proposerName}
          </div>
        )}
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-secondary, #64748b)', marginBottom: '2px' }}>
            <span>{votesFor} for / {votesAgainst} against</span>
            <span>{votesRequired} needed</span>
          </div>
          <div style={{ height: '6px', backgroundColor: 'var(--border, #334155)', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ height: '100%', backgroundColor: '#f0c040', borderRadius: '3px', width: `${pct}%`, transition: 'width 0.3s' }} />
          </div>
        </div>
        {isPending && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => handleVote(proposal.id, 'for')}
              disabled={voteLoading[proposal.id]}
              style={{
                flex: 1, padding: '6px 12px', borderRadius: '6px', border: 'none',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                backgroundColor: '#16a34a', color: '#fff',
                opacity: voteLoading[proposal.id] ? 0.6 : 1,
              }}
            >
              {voteLoading[proposal.id] ? '...' : 'Vote For'}
            </button>
            <button
              onClick={() => handleVote(proposal.id, 'against')}
              disabled={voteLoading[proposal.id]}
              style={{
                flex: 1, padding: '6px 12px', borderRadius: '6px', border: 'none',
                fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                backgroundColor: '#ef4444', color: '#fff',
                opacity: voteLoading[proposal.id] ? 0.6 : 1,
              }}
            >
              {voteLoading[proposal.id] ? '...' : 'Vote Against'}
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderBuyModal = () => {
    if (!buyModal) return null;
    const symbol = buyModal.base_currency || buyModal.symbol || buyModal.code || '';
    const price = parseFloat(buyModal.price || buyModal.priceAud || 0);
    const parsedAmount = parseFloat(buyAmount) || 0;
    const estimatedQty = price > 0 ? parsedAmount / price : 0;

    return (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '20px',
      }} onClick={() => { setBuyModal(null); setBuyAmount(''); setBuyError(''); }}>
        <div
          style={{
            backgroundColor: 'var(--surface, #1e293b)', borderRadius: '1rem',
            border: '1px solid var(--border, #334155)', padding: '24px',
            maxWidth: '400px', width: '100%',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ color: 'var(--text-primary, #fff)', margin: 0, fontSize: '18px' }}>
              Buy {symbol}
            </h3>
            <button
              onClick={() => { setBuyModal(null); setBuyAmount(''); setBuyError(''); }}
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary, #64748b)', fontSize: '20px', cursor: 'pointer' }}
            >
              x
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              width: 44, height: 44, borderRadius: '50%',
              backgroundColor: getCryptoColor(symbol), color: '#fff',
              fontSize: '20px', fontWeight: '700',
            }}>
              {getCryptoIcon(symbol)}
            </span>
            <div>
              <div style={{ color: 'var(--text-primary, #fff)', fontWeight: '600' }}>{buyModal.base_name || buyModal.name || symbol}</div>
              <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '13px' }}>Current price: {formatAud(price)}</div>
            </div>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ color: 'var(--text-secondary, #64748b)', fontSize: '13px', display: 'block', marginBottom: '4px' }}>
              Amount (AUD)
            </label>
            <input
              type="number"
              min="1"
              step="1"
              value={buyAmount}
              onChange={(e) => setBuyAmount(e.target.value)}
              placeholder="e.g. 50"
              style={{
                width: '100%', padding: '10px 12px', borderRadius: '8px',
                border: '1px solid var(--border, #334155)',
                backgroundColor: 'var(--surface-alt, #0f172a)', color: 'var(--text-primary, #fff)',
                fontSize: '15px', outline: 'none', boxSizing: 'border-box',
              }}
            />
          </div>
          {parsedAmount > 0 && price > 0 && (
            <div style={{
              backgroundColor: 'var(--surface-alt, #0f172a)', borderRadius: '8px', padding: '10px 12px', marginBottom: '12px',
              border: '1px solid var(--border, #334155)',
            }}>
              <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px' }}>Estimated quantity</div>
              <div style={{ color: 'var(--text-primary, #fff)', fontSize: '16px', fontWeight: '600' }}>
                {formatCrypto(estimatedQty)} {symbol}
              </div>
            </div>
          )}
          {buyError && <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '8px' }}>{buyError}</div>}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => { setBuyModal(null); setBuyAmount(''); setBuyError(''); }}
              style={{
                flex: 1, padding: '10px', borderRadius: '8px',
                border: '1px solid var(--border, #334155)', backgroundColor: 'transparent',
                color: 'var(--text-secondary, #64748b)', fontSize: '14px', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleCreateProposal}
              disabled={buyLoading || !buyAmount || parseFloat(buyAmount) <= 0}
              style={{
                flex: 1, padding: '10px', borderRadius: '8px', border: 'none',
                backgroundColor: (!buyAmount || parseFloat(buyAmount) <= 0) ? '#3a3d45' : '#f0c040',
                color: (!buyAmount || parseFloat(buyAmount) <= 0) ? '#888' : '#1a1d23',
                fontSize: '14px', fontWeight: '600',
                cursor: (!buyAmount || parseFloat(buyAmount) <= 0) ? 'not-allowed' : 'pointer',
                opacity: buyLoading ? 0.6 : 1,
              }}
            >
              {buyLoading ? 'Submitting...' : 'Create Proposal'}
            </button>
          </div>
          <p style={{ color: 'var(--text-secondary, #64748b)', fontSize: '11px', margin: '10px 0 0', textAlign: 'center' }}>
            This creates a proposal for the club to vote on. The trade executes when enough members approve.
          </p>
        </div>
      </div>
    );
  };

  // --- Embedded mode ---

  if (loading) {
    return embedded ? null : <div className="fund-loading">Loading crypto data...</div>;
  }

  if (embedded) {
    const activeProposals = proposals.filter((p) => p.status === 'pending' || p.status === 'open');
    const holdingsList = holdings?.holdings || holdings?.positions || [];

    return (
      <div>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ color: '#fff', margin: 0, fontSize: '16px' }}>
            <i className="fab fa-bitcoin" style={{ marginRight: '8px', color: '#f7931a' }}></i>
            Crypto Syndicate
          </h3>
        </div>

        {/* Asset price cards */}
        {assets.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: '10px', marginBottom: '16px' }}>
            {assets.map((asset) => renderAssetCard(asset, true))}
          </div>
        )}

        {/* Holdings summary */}
        {holdingsList.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '8px' }}>
              Club Holdings
            </div>
            <div style={{
              backgroundColor: 'var(--surface, #1e293b)', borderRadius: '1rem',
              border: '1px solid var(--border, #334155)', overflow: 'hidden',
            }}>
              {holdingsList.map((h) => {
                const sym = h.asset || h.symbol || '';
                const qty = parseFloat(h.totalQuantity || h.quantity || 0);
                const val = h.currentValueDollars || h.currentValueAud || (qty * parseFloat(h.spotPriceDollars || 0));
                const costBasis = h.totalCostDollars || h.costBasisAud || 0;
                const pnl = val - costBasis;
                const isUp = pnl >= 0;
                return (
                  <div key={sym} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: '1px solid var(--border, #334155)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 24, height: 24, borderRadius: '50%',
                        backgroundColor: getCryptoColor(sym), color: '#fff',
                        fontSize: '10px', fontWeight: '700',
                      }}>
                        {getCryptoIcon(sym)}
                      </span>
                      <span style={{ color: 'var(--text-primary, #fff)', fontSize: '13px', fontWeight: '600' }}>{sym}</span>
                      <span style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px' }}>{formatCrypto(qty)}</span>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: 'var(--text-primary, #fff)', fontSize: '13px', fontWeight: '600' }}>{formatAud(val)}</div>
                      <div style={{ color: isUp ? '#16a34a' : '#ef4444', fontSize: '11px' }}>
                        {isUp ? '+' : ''}{formatAud(pnl)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Active proposals */}
        {activeProposals.length > 0 && (
          <div>
            <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '8px' }}>
              Active Proposals
            </div>
            {activeProposals.map((p) => renderProposalCard(p))}
          </div>
        )}

        {/* Member Ownership */}
        {allShares.length > 0 && holdingsList.length > 0 && (
          <div>
            <button
              onClick={() => setShowMembers(!showMembers)}
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary, #64748b)', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: showMembers ? '8px' : '0' }}
            >
              <i className={`fas fa-chevron-${showMembers ? 'down' : 'right'}`} style={{ fontSize: '10px' }}></i>
              Member Ownership ({allShares.length})
            </button>
            {showMembers && (
              <div style={{ backgroundColor: 'var(--surface, #1e293b)', borderRadius: '0.75rem', border: '1px solid var(--border, #334155)', overflow: 'hidden' }}>
                {allShares.map((m, i) => (
                  <div key={m.userId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: i < allShares.length - 1 ? '1px solid var(--border, #334155)' : 'none' }}>
                    <div>
                      <div style={{ color: 'var(--text-primary, #fff)', fontSize: '13px', fontWeight: '600' }}>{m.name || 'Member'}</div>
                      <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '11px' }}>{m.ownershipPercent}% ownership</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: 'var(--text-primary, #fff)', fontSize: '13px', fontWeight: '600' }}>{formatAud(m.totalValueDollars)}</div>
                      <div style={{ color: m.totalPnlDollars >= 0 ? '#16a34a' : '#ef4444', fontSize: '11px' }}>
                        {m.totalPnlDollars >= 0 ? '+' : ''}{formatAud(m.totalPnlDollars)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Transaction History */}
        {transactions.length > 0 && (
          <div>
            <button
              onClick={() => setShowHistory(!showHistory)}
              style={{ background: 'none', border: 'none', color: 'var(--text-secondary, #64748b)', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: showHistory ? '8px' : '0' }}
            >
              <i className={`fas fa-chevron-${showHistory ? 'down' : 'right'}`} style={{ fontSize: '10px' }}></i>
              Purchase History ({transactions.length})
            </button>
            {showHistory && (
              <div style={{ backgroundColor: 'var(--surface, #1e293b)', borderRadius: '0.75rem', border: '1px solid var(--border, #334155)', overflow: 'hidden' }}>
                {transactions.map((tx, i) => (
                  <div key={tx.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderBottom: i < transactions.length - 1 ? '1px solid var(--border, #334155)' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 24, height: 24, borderRadius: '50%',
                        backgroundColor: getCryptoColor(tx.asset), color: '#fff',
                        fontSize: '10px', fontWeight: '700',
                      }}>
                        {getCryptoIcon(tx.asset)}
                      </span>
                      <div>
                        <div style={{ color: 'var(--text-primary, #fff)', fontSize: '13px', fontWeight: '600' }}>
                          {tx.type === 'buy' ? 'Bought' : 'Sold'} {formatCrypto(tx.quantity)} {tx.asset}
                        </div>
                        <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '11px' }}>
                          {new Date(tx.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} — {tx.executedBy?.name || 'Unknown'}
                        </div>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: 'var(--text-primary, #fff)', fontSize: '13px', fontWeight: '600' }}>{formatAud(tx.totalDollars)}</div>
                      <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '11px' }}>@ {formatAud(tx.pricePerUnitDollars)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {assets.length === 0 && holdingsList.length === 0 && activeProposals.length === 0 && (
          <div style={{
            backgroundColor: 'var(--surface, #1e293b)', borderRadius: '1rem',
            border: '1px solid var(--border, #334155)', padding: '2rem', textAlign: 'center',
          }}>
            <p style={{ color: 'var(--text-secondary, #94a3b8)', margin: 0, fontSize: '14px' }}>
              No crypto data available yet.
            </p>
          </div>
        )}

        {renderBuyModal()}
      </div>
    );
  }

  // --- Full mode with tabs ---

  const holdingsList = holdings?.holdings || holdings?.positions || [];
  const totalPortfolioValue = holdingsList.reduce((sum, h) => sum + (h.currentValueDollars || h.currentValueAud || 0), 0);
  const totalCostBasis = holdingsList.reduce((sum, h) => sum + (h.totalCostDollars || h.costBasisAud || 0), 0);
  const totalPnl = totalPortfolioValue - totalCostBasis;
  const totalPnlPct = totalCostBasis > 0 ? (totalPnl / totalCostBasis) * 100 : 0;
  const activeProposals = proposals.filter((p) => p.status === 'pending' || p.status === 'open');
  const pastProposals = proposals.filter((p) => p.status !== 'pending' && p.status !== 'open');

  const tabs = [
    { key: 'markets', label: 'Markets' },
    { key: 'holdings', label: 'Holdings' },
    { key: 'proposals', label: 'Proposals' },
    { key: 'history', label: 'History' },
  ];

  return (
    <div>
      <h2 style={{ color: '#fff', marginBottom: '4px' }}>Crypto Syndicate</h2>
      <p style={{ color: '#888', fontSize: '14px', marginTop: 0, marginBottom: '20px' }}>
        Pool funds with your club to invest in crypto together.
      </p>

      {/* Tab nav */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '1px solid var(--border, #334155)' }}>
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 16px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer',
              color: activeTab === tab.key ? '#f0c040' : '#888', fontSize: '14px',
              borderBottom: activeTab === tab.key ? '2px solid #f0c040' : '2px solid transparent',
              marginBottom: '-1px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Markets Tab */}
      {activeTab === 'markets' && (
        <div>
          <h3 style={{ color: '#fff', fontSize: '16px', marginBottom: '12px' }}>Crypto Markets</h3>
          {assets.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', backgroundColor: 'var(--surface, #1e293b)', borderRadius: '10px', border: '1px solid var(--border, #334155)' }}>
              <p style={{ color: '#888' }}>No assets available.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>
              {assets.map((asset) => renderAssetCard(asset))}
            </div>
          )}
        </div>
      )}

      {/* Holdings Tab */}
      {activeTab === 'holdings' && (
        <div>
          <h3 style={{ color: '#fff', fontSize: '16px', marginBottom: '12px' }}>Club Holdings</h3>

          {/* Portfolio summary */}
          {holdingsList.length > 0 && (
            <div style={{
              display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap',
            }}>
              <div style={{
                flex: '1 1 200px', backgroundColor: 'var(--surface, #1e293b)', borderRadius: '1rem',
                border: '1px solid var(--border, #334155)', padding: '16px',
              }}>
                <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px', marginBottom: '4px' }}>Total Portfolio Value</div>
                <div style={{ color: 'var(--text-primary, #fff)', fontSize: '24px', fontWeight: '700' }}>{formatAud(totalPortfolioValue)}</div>
                <div style={{ color: totalPnl >= 0 ? '#16a34a' : '#ef4444', fontSize: '14px', fontWeight: '600' }}>
                  {totalPnl >= 0 ? '+' : ''}{formatAud(totalPnl)} ({totalPnl >= 0 ? '+' : ''}{totalPnlPct.toFixed(1)}%)
                </div>
              </div>
              {memberShare && (
                <div style={{
                  flex: '1 1 200px', backgroundColor: 'var(--surface, #1e293b)', borderRadius: '1rem',
                  border: '1px solid var(--border, #334155)', padding: '16px',
                }}>
                  <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px', marginBottom: '4px' }}>My Share</div>
                  <div style={{ color: 'var(--text-primary, #fff)', fontSize: '24px', fontWeight: '700' }}>
                    {formatAud(memberShare.shareValueAud || memberShare.value || 0)}
                  </div>
                  <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '13px' }}>
                    {((memberShare.sharePercent || memberShare.percentage || 0) * 100).toFixed(1)}% of portfolio
                  </div>
                </div>
              )}
            </div>
          )}

          {holdingsList.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', backgroundColor: 'var(--surface, #1e293b)', borderRadius: '10px', border: '1px solid var(--border, #334155)' }}>
              <p style={{ color: '#888' }}>No holdings yet. Create a buy proposal in the Markets tab to get started.</p>
            </div>
          ) : (
            <div style={{
              backgroundColor: 'var(--surface, #1e293b)', borderRadius: '10px', border: '1px solid var(--border, #334155)', overflow: 'hidden',
            }}>
              <div style={{
                display: 'flex', padding: '10px 16px', borderBottom: '1px solid var(--border, #334155)',
                backgroundColor: 'var(--surface-alt, #334155)', gap: '8px', flexWrap: 'wrap',
              }}>
                <span style={{ flex: '1 1 120px', fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Asset</span>
                <span style={{ flex: '1 1 80px', fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Qty</span>
                <span style={{ flex: '1 1 80px', fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Avg Price</span>
                <span style={{ flex: '1 1 80px', fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Price</span>
                <span style={{ flex: '1 1 80px', fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Value</span>
                <span style={{ flex: '1 1 90px', fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase', textAlign: 'right' }}>P&L</span>
              </div>
              {holdingsList.map((h) => renderHoldingRow(h))}
            </div>
          )}
        </div>
      )}

      {/* Proposals Tab */}
      {activeTab === 'proposals' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ color: '#fff', fontSize: '16px', margin: 0 }}>Proposals</h3>
            <button
              onClick={() => setActiveTab('markets')}
              style={{
                padding: '6px 14px', borderRadius: '6px', border: '1px solid #f0c040',
                backgroundColor: 'transparent', color: '#f0c040', fontSize: '13px', cursor: 'pointer',
              }}
            >
              + New Proposal
            </button>
          </div>

          {activeProposals.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '8px' }}>
                Active
              </div>
              {activeProposals.map((p) => renderProposalCard(p))}
            </div>
          )}

          {activeProposals.length === 0 && (
            <div style={{ textAlign: 'center', padding: '30px', backgroundColor: 'var(--surface, #1e293b)', borderRadius: '10px', border: '1px solid var(--border, #334155)', marginBottom: '20px' }}>
              <p style={{ color: '#888' }}>No active proposals. Go to Markets to create one.</p>
            </div>
          )}

          {pastProposals.length > 0 && (
            <div>
              <div style={{ color: 'var(--text-secondary, #64748b)', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', marginBottom: '8px' }}>
                Past Proposals
              </div>
              {pastProposals.map((p) => renderProposalCard(p))}
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div>
          <h3 style={{ color: '#fff', fontSize: '16px', marginBottom: '12px' }}>Transaction History</h3>
          {transactions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', backgroundColor: 'var(--surface, #1e293b)', borderRadius: '10px', border: '1px solid var(--border, #334155)' }}>
              <p style={{ color: '#888' }}>No transactions yet.</p>
            </div>
          ) : (
            <div style={{
              backgroundColor: 'var(--surface, #1e293b)', borderRadius: '10px', border: '1px solid var(--border, #334155)', overflow: 'hidden',
            }}>
              <div style={{
                display: 'flex', padding: '10px 16px', borderBottom: '1px solid var(--border, #334155)',
                backgroundColor: 'var(--surface-alt, #334155)', gap: '8px',
              }}>
                <span style={{ flex: '1 1 100px', fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Date</span>
                <span style={{ flex: '0 0 60px', fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Type</span>
                <span style={{ flex: '0 0 60px', fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Asset</span>
                <span style={{ flex: '1 1 80px', fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Qty</span>
                <span style={{ flex: '1 1 80px', fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Price</span>
                <span style={{ flex: '1 1 80px', fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase' }}>Total</span>
                <span style={{ flex: '0 0 70px', fontSize: '12px', color: '#888', fontWeight: '600', textTransform: 'uppercase', textAlign: 'right' }}>Status</span>
              </div>
              {transactions.map((tx, idx) => {
                const isBuy = (tx.type || tx.action || '').toLowerCase() === 'buy';
                return (
                  <div key={tx.id || idx} style={{
                    display: 'flex', padding: '12px 16px', borderBottom: '1px solid #1f2229',
                    gap: '8px', alignItems: 'center',
                  }}>
                    <span style={{ flex: '1 1 100px', color: 'var(--text-secondary, #64748b)', fontSize: '13px' }}>
                      {tx.createdAt || tx.date ? new Date(tx.createdAt || tx.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' }) : '-'}
                    </span>
                    <span style={{
                      flex: '0 0 60px', fontSize: '12px', fontWeight: '600',
                      color: isBuy ? '#16a34a' : '#ef4444',
                    }}>
                      {(tx.type || tx.action || '-').toUpperCase()}
                    </span>
                    <span style={{ flex: '0 0 60px', color: 'var(--text-primary, #fff)', fontSize: '13px', fontWeight: '600' }}>
                      {tx.asset || tx.symbol || '-'}
                    </span>
                    <span style={{ flex: '1 1 80px', color: 'var(--text-primary, #fff)', fontSize: '13px' }}>
                      {tx.quantity ? formatCrypto(tx.quantity) : '-'}
                    </span>
                    <span style={{ flex: '1 1 80px', color: 'var(--text-primary, #fff)', fontSize: '13px' }}>
                      {tx.priceAud ? formatAud(tx.priceAud) : '-'}
                    </span>
                    <span style={{ flex: '1 1 80px', color: 'var(--text-primary, #fff)', fontSize: '13px', fontWeight: '600' }}>
                      {tx.totalCents ? formatCurrency(tx.totalCents) : tx.totalAud ? formatAud(tx.totalAud) : '-'}
                    </span>
                    <span style={{
                      flex: '0 0 70px', textAlign: 'right', fontSize: '11px', fontWeight: '600',
                      color: tx.status === 'completed' || tx.status === 'executed' ? '#16a34a' : tx.status === 'failed' ? '#ef4444' : '#f0c040',
                    }}>
                      {tx.status || '-'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {renderBuyModal()}
    </div>
  );
}

export default ClubCrypto;
